import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, User, Mail, Phone, FileText, Hash, CreditCard, ShoppingCart } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { authService, userService, transactionService } from '../services/api'
import RegisterModal from './RegisterModal'
import Login from './Login'
import toast from 'react-hot-toast'

const PaymentModal = ({ isOpen, onClose, raffle, selectedPackage, customQuantity, packages }) => {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [hasExistingUser, setHasExistingUser] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    typedoc: 'cedula',
    doc: ''
  })
  const [isProcessing, setIsProcessing] = useState(false)

  // Verificar autenticación al abrir el modal
  useEffect(() => {
    if (isOpen) {
      checkAuthentication()
    }
  }, [isOpen])

  const checkAuthentication = async () => {
    const authenticated = authService.isAuthenticated()
    setIsAuthenticated(authenticated)
    
    if (authenticated) {
      try {
        const response = await userService.getProfile()
        setUserData(response.user)
        setFormData({
          name: response.user.name || '',
          email: response.user.email || '',
          phone: response.user.phone || '',
          typedoc: response.user.typedoc || 'cedula',
          doc: response.user.doc || ''
        })
        // Si el usuario está logueado, marcar como usuario existente
        setHasExistingUser(true)
      } catch (error) {
        console.error('Error al obtener perfil:', error)
      }
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const getTicketQuantity = () => {
    if (customQuantity) return customQuantity
    if (selectedPackage === 'custom') return parseInt(customQuantity) || 1
    
    // Buscar el paquete seleccionado en el array de packages
    const selectedPkg = packages?.find(pkg => pkg.id === selectedPackage)
    return selectedPkg?.quantity || 1
  }

  const calculateTotal = () => {
    const ticketPrice = raffle?.amount || 1000
    return ticketPrice * getTicketQuantity()
  }

  const handlePayment = async () => {
    if (isProcessing) return
    
    // Validar campos requeridos
    if (!formData.name || !formData.email || !formData.phone || !formData.doc) {
      toast.error('Por favor complete todos los campos requeridos')
      return
    }
    
    setIsProcessing(true)
    
    try {
      const transactionData = {
        sorteo_id: raffle.id,
        cantidad: getTicketQuantity(),
        customer_data: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          doc: formData.doc,
          typedoc: formData.typedoc
        },
        has_existing_user: isAuthenticated || hasExistingUser
      }
      
      const response = await transactionService.createTransaction(transactionData)
      
      // Mostrar información de la transacción
      toast.success(`Transacción creada: ${response.transaction.reference}`)
      
      // Redirigir a página de resultado para esperar el resultado del pago
      navigate(`/resultado-transaccion?ref=${response.transaction.reference}`)
      
      onClose()
      
    } catch (error) {
      console.error('Error al crear transacción:', error)
      toast.error(error.message || 'Error al procesar la transacción')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleLoginSuccess = () => {
    setShowLoginModal(false)
    checkAuthentication()
  }

  const handleRegisterSuccess = () => {
    setShowRegisterModal(false)
    checkAuthentication()
  }

  if (!raffle) return null

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-gradient-to-br from-gray-900 to-black border border-white/20 rounded-2xl p-8 max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                  <CreditCard className="w-8 h-8 text-primary-400" />
                  Proceder al Pago
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Contenido principal del modal */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Columna 1: Resumen de Compra */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-primary-400" />
                    Resumen de Compra
                  </h3>
                  
                  <div className="bg-white/10 border border-white/20 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-lg font-semibold text-white">{raffle?.name}</h4>
                        <p className="text-gray-400 text-sm mt-1">
                          Compra de {getTicketQuantity()} boleto{getTicketQuantity() !== 1 ? 's' : ''}
                        </p>
                      </div>
                      {raffle?.image_url && (
                        <img 
                          src={raffle.image_url} 
                          alt={raffle.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                    </div>
                    
                    <div className="border-t border-white/20 pt-4 space-y-2">
                      <div className="flex justify-between text-gray-300">
                        <span>Precio por boleto:</span>
                        <span>${(raffle?.amount || 1000).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Cantidad:</span>
                        <span>{getTicketQuantity()} boleto{getTicketQuantity() !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex justify-between text-xl font-bold text-white border-t border-white/20 pt-2">
                        <span>Total:</span>
                        <span>${calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-blue-300 text-sm">
                        <strong>Nota:</strong> Los números de sus boletos se generarán aleatoriamente y 
                        solo serán visibles una vez completado el pago.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Columna 2: Datos de Pago */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-primary-400" />
                    Datos de Pago
                  </h3>
                  
                  {/* Checkbox para usuario existente - Solo mostrar si no está logueado */}
                  {!isAuthenticated && (
                    <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hasExistingUser}
                          onChange={(e) => setHasExistingUser(e.target.checked)}
                          className="mt-1 w-4 h-4 text-primary-500 bg-transparent border-2 border-primary-400 rounded focus:ring-primary-500 focus:ring-2"
                        />
                        <div>
                          <span className="text-white font-medium">¿Ya cuentas con usuario?</span>
                          {hasExistingUser && (
                            <p className="text-blue-300 text-sm mt-1">
                              Tus boletos serán añadidos a tu cuenta con el correo que digites en el campo email.
                            </p>
                          )}
                        </div>
                      </label>
                    </div>
                  )}
                  
                  {/* Mensaje para usuarios logueados */}
                  {isAuthenticated && (
                    <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                      <p className="text-green-300 text-sm">
                        <strong>✓ Usuario autenticado:</strong> Los boletos serán añadidos automáticamente a tu cuenta.
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    {/* Nombre */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nombre Completo
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ingrese su nombre completo"
                          required
                        />
                      </div>
                    </div>

                    {/* Tipo de Documento */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Tipo de Documento
                      </label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <select
                          name="typedoc"
                          value={formData.typedoc}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="cedula">Cédula de Ciudadanía</option>
                          <option value="cedula_extranjeria">Cédula de Extranjería</option>
                          <option value="pasaporte">Pasaporte</option>
                          <option value="tarjeta_identidad">Tarjeta de Identidad</option>
                        </select>
                      </div>
                    </div>

                    {/* Número de Documento */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Número de Documento
                      </label>
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          name="doc"
                          value={formData.doc}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ingrese su número de documento"
                          required
                        />
                      </div>
                    </div>

                    {/* Teléfono */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Teléfono
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ingrese su número de teléfono"
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Correo Electrónico
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ingrese su correo electrónico"
                          required
                        />
                      </div>
                    </div>


                  </div>
                </div>
              </div>

              {/* Footer con botón de pago */}
              <div className="mt-8 pt-6 border-t border-white/20">
                <div className="flex justify-between items-center">
                  <div className="text-gray-400">
                    <p className="text-sm">
                      Al proceder, acepta nuestros términos y condiciones
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className={`px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
                      isProcessing
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-green-500/25 shadow-lg'
                    }`}
                  >
                    {isProcessing ? 'Procesando...' : `Ir a Pagar - $${calculateTotal().toLocaleString()}`}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de Login */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/20 rounded-2xl p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Iniciar Sesión</h3>
              <button
                onClick={() => setShowLoginModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <Login onSuccess={handleLoginSuccess} />
            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setShowLoginModal(false)
                  setShowRegisterModal(true)
                }}
                className="text-primary-400 hover:text-primary-300 transition-colors"
              >
                ¿No tienes cuenta? Regístrate aquí
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Registro */}
      <RegisterModal 
        isOpen={showRegisterModal} 
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegisterSuccess}
      />
    </>
  )
}

export default PaymentModal